import NutritionSources from "@/pages/NutritionSources";
import { Toaster } from "@/components/ui/toaster"
import { useState, useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { ActiveWorkoutProvider } from '@/lib/ActiveWorkoutContext';
import { WorkoutSummaryDialog } from '@/components/dashboard/ActiveWorkoutDialog';
import { GoalCompletionProvider, useGoalCompletion } from '@/lib/GoalCompletionContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ProtectedRoute from '@/components/ProtectedRoute';
import GoalCompletionSplash from '@/components/GoalCompletionSplash';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import AppLayout from '@/components/layout/AppLayout';
import SplashScreen from '@/components/SplashScreen';
import Dashboard from '@/pages/Dashboard';
import { Suspense, lazy } from 'react';
import SessionScheduler from '@/pages/SessionScheduler';
// VideoCall pulls in the full Agora SDK (≈350 KB) which we don't want
// blocking initial app start. Lazy-loaded so the SDK only loads when the
// user actually opens a video session.
const VideoCall = lazy(() => import('@/pages/VideoCall'));
import Progress from '@/pages/Progress';
import Workouts from '@/pages/Workouts';
import Meals from '@/pages/Meals';
import Members from '@/pages/Members';
import Admin from '@/pages/Admin';
import Account from '@/pages/Account';
import Chat from '@/pages/Chat';
import TrainerClients from '@/pages/TrainerClients';
import Onboarding from '@/pages/Onboarding';
import WorkoutTemplates from '@/pages/WorkoutTemplates';
import ExerciseLibrary from '@/pages/ExerciseLibrary';
import Library from '@/pages/Library';
import LibraryPlanDetail from '@/pages/LibraryPlanDetail';
import Settings from '@/pages/Settings';
import MyGoals from '@/pages/MyGoals';
import About from '@/pages/About';
import MyTrainer from '@/pages/MyTrainer';
import AICoach from '@/pages/AICoach';
import AuthCallback from '@/pages/AuthCallback';
import AuthExchange from '@/pages/AuthExchange';
import { normaliseTier } from '@/lib/membershipUtils';
import { base44 } from '@/api/base44Client';

// Only paid coaching tiers go through the survey.
const ONBOARDING_TIERS = new Set(['strength', 'elite_coaching']);

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin, user, refreshUser } = useAuth();
  const { completedGoal, setCompletedGoal } = useGoalCompletion();
  const [showSplash, setShowSplash] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const tierKey = normaliseTier(user?.membership_type);
  const isStaff = user?.role === "admin" || user?.role === "trainer";
  const needsOnboarding =
    !!user && !user.onboarding_complete && !isStaff && ONBOARDING_TIERS.has(tierKey);
  // Foundation/Momentum users (and staff) never see onboarding — silently mark complete on first login.
  useEffect(() => {
    if (!user) return;
    if (!user.onboarding_complete && (isStaff || !ONBOARDING_TIERS.has(tierKey))) {
      base44.auth
        .updateMe({ onboarding_complete: true })
        .then(() => refreshUser && refreshUser())
        .catch(() => { /* fire-and-forget */ });
    }
  }, [user, tierKey, isStaff, refreshUser]);

  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
      </div>
    );
  }

  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      navigateToLogin();
      return null;
    }
  }

  return (
    <>
      {showSplash && <SplashScreen onDone={() => setShowSplash(false)} />}
      {completedGoal && (
        <GoalCompletionSplash
          goalType={completedGoal}
          onComplete={() => setCompletedGoal(null)}
        />
      )}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/exchange" element={<AuthExchange />} />
        <Route element={<ProtectedRoute unauthenticatedElement={<Navigate to="/login" replace />} />}>
          <Route path="/onboarding" element={<Onboarding />} />
          <Route element={<AppLayout />}><Route path="/nutrition-sources" element={<NutritionSources />} /></Route>
          {!needsOnboarding && (
            <>
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/:section" element={<Admin />} />
              <Route element={<AppLayout />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/sessions" element={<SessionScheduler />} />
                <Route path="/progress" element={<Progress />} />
                <Route path="/workouts" element={<Workouts />} />
                <Route path="/meals" element={<Meals />} />
                <Route path="/members" element={<Members />} />
                <Route path="/account" element={<Account />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/my-goals" element={<MyGoals />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/trainer-clients" element={<TrainerClients />} />
              <Route path="/workout-templates" element={<WorkoutTemplates />} />
              <Route path="/exercise-library" element={<ExerciseLibrary />} />
              <Route path="/library" element={<Library />} />
              <Route path="/library/:id" element={<LibraryPlanDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/my-trainer" element={<MyTrainer />} />
              <Route path="/ai-coach" element={<AICoach />} />
              <Route
                path="/video-call/:sessionId"
                element={
                  <Suspense fallback={<div className="min-h-[100dvh] bg-[#020617] grid place-items-center text-white/60 text-sm">Loading video call…</div>}>
                    <VideoCall />
                  </Suspense>
                }
              />
            </Route>
            </>
          )}
          {needsOnboarding && <Route path="*" element={<Navigate to="/onboarding" replace />} />}
        </Route>
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <ActiveWorkoutProvider>
        <GoalCompletionProvider>
          <QueryClientProvider client={queryClientInstance}>
            <Router>
              <AuthenticatedApp />
              {/* Workout summary popup — mounted globally so it survives the
                  unmount of the timer pill that fires when a workout ends. */}
              <WorkoutSummaryDialog />
            </Router>
            <Toaster />
          </QueryClientProvider>
        </GoalCompletionProvider>
      </ActiveWorkoutProvider>
    </AuthProvider>
  )
}

export default App